import type { ProjectState, ProjectStore } from '..';
import type { ProjectContextValue } from '@core/context';
import type { ProjectSchema } from '@readme/backend/models/project/types';
import type { PlanConfig, PlanConfigKey, PlanDetails, PlanFeatureKey, PlanFeatures } from '@readme/plans';
import type { RequiredDeep } from 'type-fest';
import type { StateCreator } from 'zustand';

import { isTrialEnabled, isTrialPeriodExpired } from '@readme/backend/iso';
import { getFeaturePlan, getPlanFeature, getProjectPlan, PLAN_NAME } from '@readme/plans';

import { actionLog } from '@core/store/util';

import initialState from './initial';

/**
 * Hash map of `Project` section modules to `Plan` features to easily look up
 * the plan feature associated to the project module.
 */
const projectModuleFeatureMap = {
  changelog: 'changelogs',
  custompages: 'custom_pages',
  discuss: 'discussions',
  docs: 'guides',
  examples: '',
  graphql: '',
  landing: 'landing_page',
  reference: 'reference',
  suggested_edits: '',
  tutorials: 'recipes',
} satisfies Record<keyof ProjectSchema['modules'], PlanFeatureKey | ''>;

/**
 * Stub out a mock plan detail hash map that we can use to perform O(1) lookups
 * to determine if a particular key is a plan detail key or not.
 */
const planDetailMap = {
  cost: 0,
  createdAt: new Date(),
  endDate: new Date(),
  is_active: false,
  legacy: false,
  members: 0,
  name: '',
} satisfies RequiredDeep<PlanDetails>;

/**
 * Determines if the provided plan config key is a plan detail key or not.
 */
function isPlanDetail<Key extends PlanConfigKey | string>(key: Key) {
  return key in planDetailMap;
}

// import { actionLog } from '@core/store/util';

export interface ProjectPlanSliceState {
  /**
   * Project plan that is currently active. This plan is what's used to lookup
   * plan config values and features. This plan may be different than the plan
   * that is actually assigned, like when a plan override exists.
   */
  activePlan: PLAN_NAME;

  /**
   * Plan config details like `cost`, `members`, `name`, etc.
   */
  details: PlanDetails;

  /**
   * Plan config features like `guides`, `glossary`, `html`, etc. mapped to
   * `boolean` values that indicates whether the feature is enabled for this
   * plan. When `isTrial` is enabled, all plan features are overridden to be
   * enabled by default.
   */
  features: PlanFeatures;

  /**
   * Whether project is in an active trial state or not. Determines this by
   * evaluating more than just trial period but also whether the project is
   * activated and on a free plan.
   */
  isTrial: boolean;

  /**
   * Whether a project's trial period has expired. Returns `true` to indicate
   * that the trial period has expired.
   */
  isTrialExpired: boolean;

  /**
   * Project plan override. If set, it means the currently assigned `plan` is
   * being overridden. See `activePlan` for what's currently active.
   */
  override?: PLAN_NAME;

  /**
   * Project plan that is currently assigned. This is not the plan that is
   * used to derive plan config values and features. See `activePlan` instead.
   */
  plan: PLAN_NAME;
}

interface ProjectPlanSliceActions {
  /**
   * Initializes the plan state slice with the provided project data.
   */
  initialize: (project: ProjectContextValue['project']) => void;

  /**
   * Re-initializes the plan state slice using the v2 API project data. Use
   * this when the full legacy project representation is not available, such as
   * after an SWR revalidation via `ConnectProjectStoreToApi`.
   */
  initializeFromApiData: (projectData: ProjectState) => void;

  /**
   * Determines whether a project's section module (`landing`, `docs`, etc)
   * is enabled by the current plan or trial.
   */
  isProjectModuleEnabled: (module: keyof ProjectSchema['modules']) => boolean;

  /**
   * Determines whether the provided plan feature is disabled but is in an
   * active trial mode that temporarily allows the feature to be enabled. When
   * this returns `true` for a feature, a trial badge or banner should be
   * displayed alongside it.
   */
  showTrialIndicator: (feature?: keyof PlanFeatures) => boolean;
}

export interface ProjectPlanSlice {
  /**
   * State slice containing fields with plan information for the current project like whether the plan is
   * within an active trial window or to determine what plan features are enabled.
   */
  plan: ProjectPlanSliceActions & ProjectPlanSliceState;
}

/**
 * Plan state slice containing fields related to the Project plan.
 */
export const createProjectPlanSlice: StateCreator<
  ProjectPlanSlice & ProjectStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ProjectPlanSlice
> = (set, get) => ({
  plan: {
    ...initialState,

    initialize: project => {
      set(
        state => {
          // @ts-expect-error I'm not sure how to reconcile the ProjectClientSide type with the ProjectSchema
          // that is required in the iso methods
          const isTrial = isTrialEnabled(project);
          // @ts-expect-error I'm not sure how to reconcile the ProjectClientSide type with the ProjectSchema
          // that is required in the iso methods
          const isTrialExpired = isTrialPeriodExpired(project);
          const activePlan = getProjectPlan(project);
          const planConfig = getPlanFeature(activePlan);

          const applyTrial = <T extends PlanConfigKey>(feature: PlanConfig[T]) => {
            return isTrial && typeof feature === 'boolean' ? true : feature;
          };

          const val = Object.entries(planConfig).reduce(
            (config, [key, value]) => {
              const slot = isPlanDetail(key) ? 'details' : 'features';
              const patchedValue = isPlanDetail(key) ? value : applyTrial(value);
              return {
                ...config,
                [slot]: {
                  ...config[slot],
                  [key]: patchedValue,
                },
              };
            },
            { details: {} as PlanDetails, features: {} as PlanFeatures },
          );

          state.plan = {
            ...state.plan,
            activePlan,
            isTrial,
            isTrialExpired,
            plan: project.plan,
            override: project.planOverride || undefined,
            ...val,
          };
        },
        false,
        actionLog('plan.initialize', project),
      );
    },

    initializeFromApiData: projectData => {
      set(
        state => {
          const planType = projectData.plan.type as PLAN_NAME;
          const planOverride = projectData.plan.override as PLAN_NAME | null;
          const isTrialExpired = projectData.plan.trial.expired;
          const isTrial = projectData.plan.trial.active ?? false;

          // For trial projects, plan.type is already the effective plan (e.g. pro2026)
          // from the backend's getProjectPlan logic, so use it directly.
          const activePlan = planOverride || planType;
          const planConfig = getPlanFeature(activePlan);

          const applyTrial = <T extends PlanConfigKey>(feature: PlanConfig[T]) => {
            return isTrial && typeof feature === 'boolean' ? true : feature;
          };

          const val = Object.entries(planConfig).reduce(
            (config, [key, value]) => {
              const slot = isPlanDetail(key) ? 'details' : 'features';
              const patchedValue = isPlanDetail(key) ? value : applyTrial(value);
              return {
                ...config,
                [slot]: {
                  ...config[slot],
                  [key]: patchedValue,
                },
              };
            },
            { details: {} as PlanDetails, features: {} as PlanFeatures },
          );

          state.plan = {
            ...state.plan,
            activePlan,
            isTrial,
            isTrialExpired,
            plan: planType,
            override: planOverride || undefined,
            ...val,
          };
        },
        false,
        actionLog('plan.initializeFromApiData', projectData),
      );
    },

    isProjectModuleEnabled: module => {
      // Check for the exception where a modules's access is controlled only by
      // a feature flag and not by the project's current plan.
      // We perform checks for these modules separately from the plan in @core/hooks/useFeatureOptions
      const isFeatureFlagEnabled = module === 'graphql';
      if (isFeatureFlagEnabled) return true;

      const feature = projectModuleFeatureMap[module];
      const features = get().plan.features;
      return !!features?.[feature];
    },

    showTrialIndicator: feature => {
      if (!feature) return false;

      // If the feature belongs to the startup plan, we don't want to show the
      // trial indicator. We decided to do this to avoid cluttering the UI
      // with badges during a trial.
      const isStartupFeature = getFeaturePlan(feature) === PLAN_NAME.STARTUP_2018;
      return get().plan.isTrial && !isStartupFeature;
    },
  },
});
